import { GridFields, HiddenBoxStyled } from 'components';
import { useField } from 'libs/form/fields/useField';
import { useTranslation } from 'libs/i18n';
import { Stack, Typography } from 'libs/ui';
import { Colors } from 'libs/ui/theme';
import {
  Field,
  FinancialPositionFormType,
  useFormContext,
} from 'modules/client/client/forms/financialPositionForm';

type NestedKeys<T> = {
  [K in keyof T]: T[K] extends object
    ? `${K & string}.${keyof T[K] & string}`
    : never;
}[keyof T];

type FinancialPositionFormPaths = NestedKeys<
  FinancialPositionFormType['financialPosition']
>;

interface Props {
  sectionName: FinancialPositionFormPaths;
}

export const SectionBox = ({ sectionName }: Props) => {
  const parentPath = sectionName.split(
    '.'
  )[0] as keyof FinancialPositionFormType['financialPosition'];

  const childPath = sectionName.split('.')[1];

  const { t } = useTranslation();

  const { field } = useField(`financialPosition.${sectionName}.enabled`);

  const { readOnly } = useFormContext();
  const editable = !readOnly;

  const options = t(
    `client.client.financialPosition.${parentPath}.amountField.types`,
    { returnObjects: true }
  );

  const enabled = !!field.value;

  if (!editable && !enabled) {
    return null;
  }

  return (
    <>
      <Stack alignItems="center" direction="row" spacing={12}>
        <Typography color={Colors.gray700} variant="bodyMedium">
          {t(`client.client.financialPosition.${sectionName}.title`)}
        </Typography>
        {editable && (
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            spacing={16}
          >
            <Typography variant="bodyMedium">{t('common.choose')}</Typography>

            <Field.Switch name={`financialPosition.${sectionName}.enabled`} />
          </Stack>
        )}
      </Stack>
      <HiddenBoxStyled hidden={!enabled}>
        <GridFields>
          {childPath === 'other' && (
            <Field.Text
              fullWidth
              showRequiredMark
              label={t(
                `client.client.financialPosition.${parentPath}.other.otherField.label`
              )}
              name={`financialPosition.${parentPath}.other.otherText`}
              placeholder={t(
                `client.client.financialPosition.${parentPath}.other.otherField.placeholder`
              )}
            />
          )}
          <Field.Autocomplete
            showRequiredMark
            label={t(
              `client.client.financialPosition.${parentPath}.amountField.label`
            )}
            name={`financialPosition.${sectionName}.value`}
            optionLabelKey="label"
            options={Object.keys(options).map((v) => ({
              label: t(
                // @ts-ignore
                `client.client.financialPosition.${parentPath}.amountField.types.${v}`
              ),
              value: v,
            }))}
            optionValueKey="value"
            placeholder={t(
              `client.client.financialPosition.${parentPath}.amountField.placeholder`
            )}
            variant="select"
          />
        </GridFields>
      </HiddenBoxStyled>
    </>
  );
};
